import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';

import { CatalogItem, NewPurchaseOrderConfig } from '../../../../models/makers-finances-mgr.models';
import {
  DetailMode, SourceType,
  PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION,
} from '../../purchase-order.constants';
import { SelectOption, staticSourceFromCatalog } from '../section-base.types';

@Component({
  selector: 'app-section-additional-info',
  templateUrl: './section-additional-info.component.html',
  styleUrls: ['./section-additional-info.component.scss'],
})
export class SectionAdditionalInfoComponent implements OnInit, OnChanges {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  @Input() tagsCatalog: CatalogItem[] = [];
  @Input() catalogsLoaded = false;
  @Input() submitted = false;
  @Input() fieldConfigs: Record<string, any> = {};
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the tags/request-reason selects (one-select is not form-bound).
  @Input() poLoaded = false;

  @ViewChild('tagsSelect') tagsSelect: SelectComponent<SelectOption>;
  @ViewChild('requestReasonSelect') requestReasonSelect: SelectComponent<SelectOption>;

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.ADDITIONAL_INFO];

  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly tagsSelectConfig = { search: true, includeAll: false, showTotalsChip: true, maxNumberOfChips: 10, panelClass: '' };

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};

  ngOnInit(): void {
    this.form.get(PO_FIELD.DETAIL_BRIEF)?.setValidators([Validators.maxLength(300)]);
    this.form.get(PO_FIELD.DETAIL_BRIEF)?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['remoteConfig'] && this.remoteConfig) {
      if (this.remoteConfig.request_reason_options?.length) {
        this.selectSources[PO_FIELD.REQUEST_REASON] = staticSourceFromCatalog(this.remoteConfig.request_reason_options);
      }
    }

    if (changes['tagsCatalog'] && this.tagsCatalog.length) {
      this.selectSources[PO_FIELD.TAGS] = staticSourceFromCatalog(this.tagsCatalog);
    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => this._prefillSelects());
    }
  }

  // Edit mode: sync the tags/request-reason select UIs to the patched form values. The form already
  // holds the codes; the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    const tagCodes: string[] = this.form.get(PO_FIELD.TAGS)?.value ?? [];
    if (tagCodes.length && this.tagsSelect) {
      for (const code of tagCodes) {
        const item = this.tagsCatalog.find(t => t.code === code);
        if (item) this.tagsSelect.setMultipleSelection({ id: item.code, name: item.name });
      }
    }

    const reasonCode = this.form.get(PO_FIELD.REQUEST_REASON)?.value;
    if (reasonCode && this.requestReasonSelect && this.remoteConfig?.request_reason_options?.length) {
      const item = this.remoteConfig.request_reason_options.find(o => o.code === reasonCode);
      if (item) this.requestReasonSelect.setSelection({ id: item.code, name: item.name });
    }
  }

  get isRequestReasonVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.REQUEST_REASON]?.visible_by_source?.[this.sourceType];
  }

  get isTagsVisible(): boolean {
    return !!(this.tagsCatalog?.length);
  }

  get detailBriefLength(): number {
    return (this.form?.get(PO_FIELD.DETAIL_BRIEF)?.value || '').length;
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
  }

  onTagsChange(value: SelectOption | SelectOption[]): void {
    const ids = Array.isArray(value)
      ? value.map(v => v.id)
      : value ? [value.id] : [];
    this.form.get(PO_FIELD.TAGS)?.setValue(ids.length ? ids : null);
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) return false;
    return !!this.form.get(key)?.errors?.['required'];
  }
}
